import { motion } from "framer-motion";
import { Sparkles, Upload, Camera, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import planteProbleme from "@/assets/plante-probleme.webp";

/**
 * High-fidelity static capture mimicking PlantImageAnalysis.tsx from the real dashboard.
 * Uses the actual Card styling and layout to feel authentic.
 */
const FeatureScanCapture = () => {
  const diagnosisText = `🔍 **Diagnostic :**
Taches jaunâtres sur les feuilles basses, signes d'un début de carence en azote (N). La plante montre également un léger stress hydrique.

💊 **Traitement recommandé :**
Apportez un engrais organique riche en azote (type purin d'ortie ou sang séché). Dosage : 5g/L en arrosage, 1x/semaine pendant 3 semaines.

🌱 **Prévention :**
Paillez le pied pour maintenir l'humidité du sol et réduire l'évaporation. Vérifiez le drainage.`;

  return (
    <div className="bg-card rounded-2xl shadow-lg border border-primary/20 overflow-hidden bg-gradient-to-br from-background to-primary/5">
      {/* Card Header — matches PlantImageAnalysis */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            <span className="text-lg font-semibold text-foreground">Analyse IA de Plante</span>
          </div>
          <div className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-muted cursor-pointer">
            <X className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Téléchargez une photo pour un diagnostic intelligent assisté par IA
        </p>
      </div>

      {/* Card Content */}
      <div className="px-4 pb-4 space-y-3">
        {/* Action buttons — matches real component */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 bg-primary text-primary-foreground rounded-md py-2 flex items-center justify-center gap-2 text-sm font-medium">
            <Upload className="w-4 h-4 shrink-0" />
            <span>Télécharger</span>
          </div>
          <div className="flex-1 border border-border text-foreground rounded-md py-2 flex items-center justify-center gap-2 text-sm font-medium">
            <Camera className="w-4 h-4 shrink-0" />
            <span>Prendre photo</span>
          </div>
        </div>

        {/* Uploaded image preview with scanning effect */}
        <motion.div
          className="relative rounded-lg overflow-hidden border border-border"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <img
            src={planteProbleme}
            alt="Plante avec feuilles jaunissantes analysée par IA"
            className="w-full h-36 sm:h-48 object-cover"
            loading="lazy"
          />
          {/* Scanning line animation */}
          <motion.div
            className="absolute inset-x-0 h-0.5 bg-primary/60 shadow-[0_0_8px_2px_hsl(var(--primary)/0.4)]"
            initial={{ top: 0 }}
            whileInView={{ top: "100%" }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 1.8, ease: "linear" }}
          />
          {/* Analyzed badge */}
          <motion.div
            className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.5, type: "spring" }}
          >
            <CheckCircle2 className="w-3 h-3" />
            Analysé
          </motion.div>
        </motion.div>

        {/* AI Diagnosis result — matches real component styling */}
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
        >
          <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-foreground">Diagnostic IA</span>
              <span className="ml-auto text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Confiance 94%
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
              {diagnosisText}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FeatureScanCapture;
